import { useContext } from "react";
import { ValidationResult } from "sprint-common-web";

import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { CreateEntityResponse } from "../common/CreateEntityResponse";
import { ApproveOrderDeliveriesRequest } from "./order/ApproveOrderDeliveriesRequest";
import { ApproveOrderDeliveryRequest } from "./order/ApproveOrderDeliveryRequest";
import { CompleteRejectedOrderRequest } from "./order/CompleteRejectedOrderRequest";
import { CreateOrderFromOrderRequestRequest } from "./order/CreateOrderFromOrderRequestRequest";
import { CreateOrderRequest } from "./order/CreateOrderRequest";
import { CreateOrdersFromImportRequest } from "./order/CreateOrdersFromImportRequest";
import { DeleteOrderRequest } from "./order/DeleteOrderRequest";
import { ExportOrdersRequest } from "./order/ExportOrdersRequest";
import { ExportOrdersResponse } from "./order/ExportOrdersResponse";
import { GetDistributionListsLookupResponse } from "./order/GetDistributionListsLookupResponse";
import { GetOrderAuditListRequest } from "./order/GetOrderAuditListRequest";
import { GetOrderAuditListResponse } from "./order/GetOrderAuditListResponse";
import { GetOrderDetailsResponse } from "./order/GetOrderDetailsResponse";
import { GetOrderListRequest } from "./order/GetOrderListRequest";
import { GetOrderListResponse } from "./order/GetOrderListResponse";
import { CopyOrderOutputsRequest } from "./order/CopyOrderOutputsRequest";
import { CopyOrderOutputsResponse } from "./order/CopyOrderOutputsResponse";
import { MergeOrdersRequest } from "./order/MergeOrdersRequest";
import { RejectOrderRequest } from "./order/RejectOrderRequest";
import { RemoveOrderWatermarkRequest } from "./order/RemoveOrderWatermarkRequest";
import { RequestOrderWatermarkRequest } from "./order/RequestOrderWatermarkRequest";
import { UpdateOrderDeliveryFolderRequest } from "./order/UpdateOrderDeliveryFolderRequest";
import { ValidateCloudAssetsRequest } from "./order/ValidateCloudAssetsRequest";
import { ValidateDeliveryPathRequest } from "./order/ValidateDeliveryPathRequest";
import { ValidateDeliveryPathResponse } from "./order/ValidateDeliveryPathResponse";
import { ValidateOrderHasWatermarkRuleResponse } from "./order/ValidateOrderHasWatermarkRuleResponse";
import { ValidateOrderPONumberRequest } from "./order/ValidateOrderPONumberRequest";
import { ValidateOrderPONumberResponse } from "./order/ValidateOrderPONumberResponse";
import { ValidateOrdersFromImportRequest } from "./order/ValidateOrdersFromImportRequest";
import { ValidateOrdersFromImportResponse } from "./order/ValidateOrdersFromImportResponse";

export const useOrdersApi = () => {
    const { httpPost, httpPut, httpGet, httpDelete } = useAuthorizedFetch();

    const appConfigContext = useContext(AppConfigContext);

    const baseUrl = appConfigContext.ordersConfig.apiUrl;

    const ordersBaseUrl = `${baseUrl}/orders`;
    const smartjogBaseUrl = `${baseUrl}/smartjog`;

    const validateCloudAssets = async (
        request: ValidateCloudAssetsRequest,
        signal?: AbortSignal
    ): Promise<ValidationResult> => {
        return httpPost(`${ordersBaseUrl}/validate-create`, request, signal);
    };

    const createOrder = async (request: CreateOrderRequest): Promise<CreateEntityResponse> => {
        return httpPost(`${ordersBaseUrl}/create`, request);
    };

    const validateOrdersFromImport = async (
        request: ValidateOrdersFromImportRequest
    ): Promise<ValidateOrdersFromImportResponse> => {
        return httpPost(`${ordersBaseUrl}/validate-create-from-import`, request);
    };

    const createOrdersFromImport = async (request: CreateOrdersFromImportRequest): Promise<void> => {
        return httpPost(`${ordersBaseUrl}/create-from-import`, request);
    };

    const approveOrderDelivery = async (request: ApproveOrderDeliveryRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/approve-delivery`, request);
    };

    const approveOrderDeliveries = async (request: ApproveOrderDeliveriesRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/approve-deliveries`, request);
    };

    const getOrderList = async (request: GetOrderListRequest): Promise<GetOrderListResponse> => {
        return httpGet<GetOrderListResponse>(`${ordersBaseUrl}/list`, request);
    };

    const getOrderDetails = async (orderId: string): Promise<GetOrderDetailsResponse> => {
        return httpGet<GetOrderDetailsResponse>(`${ordersBaseUrl}/details/${orderId}`);
    };

    const validateOrderPONumber = async (
        request: ValidateOrderPONumberRequest
    ): Promise<ValidateOrderPONumberResponse> => {
        return httpGet(`${ordersBaseUrl}/validate-po-number`, request);
    };

    const validateDeliveryPath = async (
        request: ValidateDeliveryPathRequest
    ): Promise<ValidateDeliveryPathResponse> => {
        return httpGet(`${ordersBaseUrl}/validate-delivery-path`, request);
    };

    const getDistributionListsLookup = async (): Promise<GetDistributionListsLookupResponse> => {
        return httpGet<GetDistributionListsLookupResponse>(`${smartjogBaseUrl}/distribution-lists/get-lookup`);
    };

    const exportOrders = async (request: ExportOrdersRequest): Promise<ExportOrdersResponse> => {
        return httpPost(`${ordersBaseUrl}/export`, request);
    };

    const mergeOrders = async (request: MergeOrdersRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/merge`, request);
    };

    const rejectOrder = async (request: RejectOrderRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/reject`, request);
    };

    const completeRejectedOrder = async (request: CompleteRejectedOrderRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/complete-rejected`, request);
    };

    const deleteOrder = async (request: DeleteOrderRequest): Promise<void> => {
        return httpDelete(`${ordersBaseUrl}/delete`, request);
    };

    const copyOrderOutputs = async (request: CopyOrderOutputsRequest): Promise<CopyOrderOutputsResponse> => {
        return httpPost(`${ordersBaseUrl}/copy-outputs`, request);
    };

    const updateOrderDeliveryFolder = async (request: UpdateOrderDeliveryFolderRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/update-delivery-folder`, request);
    };

    const requestOrderWatermark = async (request: RequestOrderWatermarkRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/request-watermark`, request);
    };

    const reprocessOrder = async (request: { orderId: string }): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/reprocess`, request);
    };

    const selectRelevantVideo = async (request: { orderId: string; assetId: string }): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/select-relevant-video`, request);
    };

    const removeOrderWatermark = async (request: RemoveOrderWatermarkRequest): Promise<void> => {
        return httpPut(`${ordersBaseUrl}/remove-watermark`, request);
    };

    const validateIfOrderHasWatermarkRule = async (
        request: RemoveOrderWatermarkRequest
    ): Promise<ValidateOrderHasWatermarkRuleResponse> => {
        return httpGet(`${ordersBaseUrl}/validate-watermark-rule`, request);
    };

    const getCloudOrderAuditList = async (request: GetOrderAuditListRequest): Promise<GetOrderAuditListResponse> => {
        return httpGet(`${ordersBaseUrl}/${request.orderId}/audit-list`, request);
    };

    const createCloudOrderFromRequest = async (request: CreateOrderFromOrderRequestRequest): Promise<void> => {
        return httpPost(`${ordersBaseUrl}/create-from-order-request`, request);
    };

    return {
        createOrder,
        validateCloudAssets,
        approveOrderDelivery,
        approveOrderDeliveries,
        getOrderList,
        getOrderDetails,
        validateOrderPONumber,
        validateDeliveryPath,
        getDistributionListsLookup,
        exportOrders,
        rejectOrder,
        completeRejectedOrder,
        deleteOrder,
        copyOrderOutputs,
        updateOrderDeliveryFolder,
        requestOrderWatermark,
        removeOrderWatermark,
        validateIfOrderHasWatermarkRule,
        reprocessOrder,
        selectRelevantVideo,
        mergeOrders,
        getCloudOrderAuditList,
        createCloudOrderFromRequest,
        createOrdersFromImport,
        validateOrdersFromImport
    };
};
